import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft, Check, Sparkles } from 'lucide-react';

const OnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: "Welcome to BookingFlows! 🎉",
      content: (
        <div className="space-y-4">
          <div className="text-center py-6">
            <div className="inline-flex p-4 rounded-full bg-blue-600 mb-4">
              <Sparkles className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Let's Get You Started</h2>
            <p className="text-gray-600">
              We'll walk you through the core features to get your resources scheduling system up and running in minutes.
            </p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>💡 Quick Tour:</strong> This guide covers the essentials. You can explore more features later!
            </p>
          </div>
        </div>
      ),
      highlight: null
    },
    {
      title: "Step 1: Add Your Resources",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-cyan-50 to-blue-50 p-6 rounded-lg border border-cyan-200">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">⚓</span>
              Add Assets to Your Resources
            </h3>
            <p className="text-gray-700 mb-4">
              Start by registering your assets. Click <strong>"Resources Management" → "Assets"</strong> in the sidebar.
            </p>
            <div className="bg-white p-3 rounded border border-cyan-300">
              <p className="text-sm"><strong>Click "+ Add Asset"</strong> and provide:</p>
              <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4">
                <li>• Asset name (e.g., "Tequila Sunrise")</li>
                <li>• Capacity (max group size)</li>
                <li>• Optional: Add a photo URL for customer bookings</li>
              </ul>
            </div>
          </div>
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
            <p className="text-sm text-yellow-900">
              <strong>💡 Tip:</strong> You can set which teamMembers are allowed to operate each asset for better control.
            </p>
          </div>
        </div>
      ),
      highlight: '[data-nav="assets"]'
    },
    {
      title: "Step 2: Add Your TeamMembers",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg border border-indigo-200">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">👨‍✈️</span>
              Register Your TeamMembers
            </h3>
            <p className="text-gray-700 mb-4">
              Add your team! Go to <strong>"Resources Management" → "TeamMembers"</strong>.
            </p>
            <div className="bg-white p-3 rounded border border-indigo-300">
              <p className="text-sm"><strong>Click "+ Add TeamMember"</strong> and provide:</p>
              <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4">
                <li>• Name, email, and phone</li>
                <li>• Create a username & password (they can login!)</li>
                <li>• Set their availability schedule</li>
              </ul>
            </div>
          </div>
          <div className="bg-green-50 border-l-4 border-green-400 p-4">
            <p className="text-sm text-green-900">
              <strong>🔑 TeamMember Login:</strong> Each teamMember can log in with their username to view their own schedule and share booking links!
            </p>
          </div>
        </div>
      ),
      highlight: '[data-nav="teamMembers"]'
    },
    {
      title: "Step 3: Create Your First Booking",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg border border-green-200">
            <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
              <span className="text-2xl">📅</span>
              Schedule a Booking
            </h3>
            <p className="text-gray-700 mb-4">
              Create trips on the <strong>"Operations" → "Schedule"</strong> page.
            </p>
            <div className="bg-white p-3 rounded border border-green-300 space-y-3">
              <div>
                <p className="text-sm font-semibold">Click "+ Add Booking" and fill in:</p>
                <ul className="text-sm text-gray-600 mt-2 space-y-1 ml-4">
                  <li>• Date and time</li>
                  <li>• Select a teamMember</li>
                  <li>• Select a asset</li>
                  <li>• Party size (number of guests)</li>
                </ul>
              </div>
              <div className="border-t pt-2">
                <p className="text-sm font-semibold text-purple-700">✨ Or use AI Auto-Schedule!</p>
                <p className="text-xs text-gray-600 mt-1">
                  Click "AI Auto Schedule" to automatically assign teamMembers and assets to unassigned trips.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      highlight: '[data-nav="schedule"]'
    },
    {
      title: "Power Features Overview",
      content: (
        <div className="space-y-4">
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
            <h3 className="font-semibold text-lg mb-3">🚀 Additional Features</h3>
            <p className="text-gray-700 mb-4">You've got the basics! Here are more powerful tools:</p>
          </div>
          
          <div className="grid gap-3">
            <div className="bg-white border-l-4 border-blue-500 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-sm mb-1">📊 Bulk Import Bookings</h4>
              <p className="text-xs text-gray-600">Import from Excel, CSV, or even take a photo of your schedule!</p>
            </div>
            
            <div className="bg-white border-l-4 border-green-500 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-sm mb-1">💰 Payroll System</h4>
              <p className="text-xs text-gray-600">Calculate teamMember earnings with percentage-based tip sharing.</p>
            </div>
            
            <div className="bg-white border-l-4 border-purple-500 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-sm mb-1">🎫 Customer Bookings</h4>
              <p className="text-xs text-gray-600">Create services, embed a booking widget on your website!</p>
            </div>
            
            <div className="bg-white border-l-4 border-pink-500 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-sm mb-1">🔗 TeamMember Booking Links</h4>
              <p className="text-xs text-gray-600">TeamMembers can share personal links for direct bookings.</p>
            </div>
            
            <div className="bg-white border-l-4 border-orange-500 p-4 rounded-r-lg shadow-sm">
              <h4 className="font-semibold text-sm mb-1">📈 System Health</h4>
              <p className="text-xs text-gray-600">Monitor system status and run automated tests.</p>
            </div>
          </div>

          <div className="bg-blue-600 text-white p-4 rounded-lg">
            <p className="text-sm font-semibold mb-2">🎓 Want to learn more?</p>
            <p className="text-xs">Click the <strong>Help (?) icon</strong> in the top bar anytime for detailed guides!</p>
          </div>
        </div>
      ),
      highlight: null
    }
  ];

  const currentStepData = steps[currentStep];
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  useEffect(() => {
    // Highlight the relevant sidebar item
    if (currentStepData.highlight) {
      const element = document.querySelector(currentStepData.highlight);
      if (element) {
        element.classList.add('ring-4', 'ring-blue-500', 'ring-offset-2');
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return () => {
      // Remove highlight from previous step
      const allHighlighted = document.querySelectorAll('.ring-blue-500');
      allHighlighted.forEach(el => {
        el.classList.remove('ring-4', 'ring-blue-500', 'ring-offset-2');
      });
    };
  }, [currentStep, currentStepData.highlight]);

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
      onClose();
    } else {
      setCurrentStep(prev => Math.min(prev + 1, steps.length - 1));
    }
  };

  const handlePrev = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSkip = () => {
    onComplete();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden [&>button]:hidden">
        {/* Header with progress */}
        <div className="bg-blue-600 text-white p-6 pb-8">
          <div className="flex justify-between items-start mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-bold">{currentStepData.title}</h2>
              <p className="text-blue-100 text-sm mt-1">Step {currentStep + 1} of {steps.length}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleSkip}
              className="text-white hover:bg-white/20"
            >
              <X className="w-4 h-4 mr-1" />
              Skip
            </Button>
          </div>
          
          {/* Progress bar */}
          <div className="w-full bg-white/30 rounded-full h-2">
            <div 
              className="bg-white h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {currentStepData.content}
        </div>

        {/* Footer with navigation */}
        <div className="border-t p-4 bg-gray-50 flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrev}
            disabled={isFirstStep}
            className="gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>

          <div className="flex gap-1">
            {steps.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentStep
                    ? 'bg-blue-600 w-4'
                    : index < currentStep
                    ? 'bg-blue-400'
                    : 'bg-gray-300'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={handleNext}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
          >
            {isLastStep ? (
              <>
                <Check className="w-4 h-4" />
                Get Started
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingTutorial;
